// GAVION task actions « Contexte »: the compagnie, the task's deal and the recent history in a side sheet, so the rep
// reads the context without leaving /tasks. Mounted only while open (the timeline loads then).
import { useGetOne, useTranslate, type Identifier } from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { DealAmount } from "../deals/DealAmount";
import type { AmountRow } from "../deals/dealAmountModel";
import { StageBadge } from "../deals/StageBadge";
import { formatPhoneDisplay } from "../domain/phone";
import { ProspectStatusBadge } from "../leads/badges";
import { LeadTimeline } from "../leads/show/LeadTimeline";
import type { Lead } from "../leads/types";
import type { ActionTask } from "./taskActionTypes";

type DealRow = AmountRow & { id: Identifier; name: string; stage: string };

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm">{children}</dd>
  </div>
);

const Group = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="flex flex-col gap-3">
    <h3 className="text-[11px] font-bold uppercase tracking-[.08em] text-muted-foreground">
      {title}
    </h3>
    {children}
  </section>
);

export const TaskContextSheet = ({
  task,
  companyId,
  open,
  onOpenChange,
}: {
  task: ActionTask;
  companyId: Identifier;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const { data: company } = useGetOne<Lead>("companies", { id: companyId });
  // « deals » reads deals_visible (amount masked for a Setter), as the deal page does.
  const { data: deal } = useGetOne<DealRow>(
    "deals",
    { id: task.deal_id as Identifier },
    { enabled: task.deal_id != null },
  );
  const phone = company?.phone_e164
    ? formatPhoneDisplay(company.phone_e164)
    : company?.phone_number;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-lg">
        <SheetHeader className="border-b">
          <SheetTitle>
            {company?.name ?? translate("gavion.tasks.context.title")}
          </SheetTitle>
          <SheetDescription>{task.text}</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-6 p-4">
          <Group title={translate("gavion.tasks.context.company")}>
            {company ? (
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={translate("gavion.tasks.context.status")}>
                  <ProspectStatusBadge
                    status={company.prospect_status}
                    outcome={company.last_call_outcome}
                    calledAt={company.last_called_at}
                  />
                </Field>
                <Field label={translate("gavion.tasks.context.rep")}>
                  {company.assigned_rep_name ||
                    translate("gavion.tasks.context.unassigned")}
                </Field>
                <Field label={translate("gavion.tasks.context.phone")}>
                  {phone || "—"}
                </Field>
                <Field label={translate("gavion.tasks.context.email")}>
                  {company.email || "—"}
                </Field>
              </dl>
            ) : (
              <Skeleton className="h-16" />
            )}
            <Button asChild variant="outline" size="sm" className="self-start">
              <Link to={`/companies/${companyId}/show`}>
                {translate("gavion.tasks.context.open_company")}
              </Link>
            </Button>
          </Group>
          {task.deal_id != null ? (
            <Group title={translate("gavion.tasks.context.deal")}>
              {deal ? (
                <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Field label={translate("gavion.tasks.context.deal")}>
                      {deal.name}
                    </Field>
                  </div>
                  <Field label={translate("gavion.tasks.context.stage")}>
                    <StageBadge stage={deal.stage} />
                  </Field>
                  <Field label={translate("gavion.tasks.context.amount")}>
                    <DealAmount deal={deal} />
                  </Field>
                </dl>
              ) : (
                <Skeleton className="h-12" />
              )}
            </Group>
          ) : null}
          <Group title={translate("gavion.tasks.context.history")}>
            <LeadTimeline companyId={companyId} />
          </Group>
        </div>
      </SheetContent>
    </Sheet>
  );
};
